import { useState, useEffect, useCallback, useMemo } from 'react';
import { safeGet, safeSet } from '../../utils/safeStorage';
import { STORAGE_KEYS } from './storageKeys';

export function useCountermeasuresState() {
  const [countermeasures, setCountermeasures] = useState(() => safeGet(STORAGE_KEYS.countermeasures, []));

  useEffect(() => { safeSet(STORAGE_KEYS.countermeasures, countermeasures); }, [countermeasures]);

  const addCountermeasure = useCallback((cm) => {
    setCountermeasures((arr) => {
      // Dedup: same module + relatedId + issue
      const exists = arr.find((c) => c.module === cm.module && c.relatedId === cm.relatedId && c.issue === cm.issue);
      if (exists) return arr;
      const now = new Date();
      const due = new Date(now.getTime() + 30 * 86400_000);
      return [
        {
          id: `CM-${cm.module.toUpperCase()}-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
          module: cm.module,
          relatedId: cm.relatedId,
          riskId: cm.riskId || null,
          issue: cm.issue,
          owner: cm.owner || 'Division PIC',
          escalationOwner: 'Compliance Officer / President',
          dueDate: due.toISOString().slice(0, 10),
          createdAt: now.toISOString(),
          status: 'Open',
          ...cm,
        },
        ...arr,
      ];
    });
  }, []);

  const closeCountermeasure = useCallback((id, resolution) => {
    setCountermeasures((arr) => arr.map((c) =>
      c.id === id ? { ...c, status: 'Closed', closedAt: new Date().toISOString(), resolution } : c
    ));
  }, []);

  // Derived: enriched with daysOverdue + escalation flag
  const enriched = useMemo(() => {
    const today = Date.now();
    return countermeasures.map((c) => {
      const due = new Date(c.dueDate).getTime();
      const daysOverdue = c.status === 'Closed' ? 0 : Math.max(0, Math.floor((today - due) / 86400_000));
      return { ...c, daysOverdue, escalated: daysOverdue > 30 };
    });
  }, [countermeasures]);

  return { countermeasures: enriched, addCountermeasure, closeCountermeasure };
}
